import { useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Card, ErrorState, Loading } from '../components/ui.jsx';
import { DocumentActions } from '../components/DocumentActions.jsx';
import { IconEdit } from '../components/Icons.jsx';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { paymentTypeReceipt, t } from '../lib/i18n.js';
import { formatDate, liters, money, quantityLabel } from '../lib/format.js';
import { saleReceiptText } from '../lib/whatsapp.js';

export default function SaleReceipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useAuth();
  const { data: sale, error, loading, reload } = useApi(`/api/sales/${id}`);
  const docRef = useRef(null);

  if (loading && !sale) return <Loading />;
  if (error) {
    return (
      <>
        <PageHeader title={t.doc.saleReceipt} back="/sell" />
        <div className="page">
          <ErrorState error={error} onRetry={reload} />
        </div>
      </>
    );
  }
  if (!sale) return null;

  const amount = sale.currency === 'IQD' ? sale.amount_iqd : sale.amount_usd;
  const other = sale.currency === 'IQD' ? sale.amount_usd : sale.amount_iqd;

  return (
    <>
      <PageHeader
        title={t.doc.saleReceipt}
        subtitle={sale.receipt_no}
        back="/sell"
        actions={
          <button type="button" className="btn btn-sm btn-ghost no-print" onClick={() => navigate(`/sell/${id}/edit`)}>
            <IconEdit /> {t.common.edit}
          </button>
        }
      />

      <div className="page">
        <DocumentActions
          targetRef={docRef}
          filename={`sale-${sale.receipt_no}.pdf`}
          whatsappText={saleReceiptText(sale, settings)}
          phone={sale.customer_phone}
          countryCode={settings.whatsapp_country_code || '964'}
        />

        <div className="doc" ref={docRef}>
          <div className="doc-head">
            <img src="/logo.png" alt="" className="doc-logo" />
            <div className="brand">
              <div className="name">{settings.facility_name || t.app.name}</div>
              <div className="contact">
                {settings.facility_address}
                {settings.facility_address && settings.facility_phone ? ' · ' : ''}
                {settings.facility_phone}
              </div>
            </div>
            <div className="ref">
              <div className="kind">{t.doc.saleReceipt}</div>
              <div className="no">{sale.receipt_no}</div>
              <div className="contact">{formatDate(sale.sold_on)}</div>
            </div>
          </div>

          <div className="doc-rows">
            <Row k={t.sale.customer} v={sale.customer_name} />
            {sale.customer_phone && <Row k={t.common.phone} v={sale.customer_phone} />}
            {sale.customer_address && <Row k={t.common.address} v={sale.customer_address} />}
            <Row k={t.common.quantity} v={`${quantityLabel(sale.unit, sale.quantity)}  (${liters(sale.liters)})`} />
            {Number(sale.price_per_liter) > 0 && (
              <Row k={t.sale.pricePerLiter} v={money(sale.price_per_liter, sale.currency)} />
            )}
            <Row k={t.sale.sourceTank} v={sale.tank_name} />
            <Row k={t.shared.payment} v={paymentTypeReceipt[sale.payment_type]} />
            {Number(other) > 0 && (
              <Row k={t.shared.reference} v={money(other, sale.currency === 'IQD' ? 'USD' : 'IQD')} />
            )}
            {sale.notes && <Row k={t.common.notes} v={sale.notes} />}
          </div>

          <div className="doc-total">
            <span className="k">{t.common.total}</span>
            <span className="v">{money(amount, sale.currency)}</span>
          </div>

          <div className="doc-sign">
            <div>{t.sale.receivedBy}</div>
            <div>{t.sale.authorisedSignature}</div>
          </div>

          <div className="doc-foot">
            <span>{settings.receipt_footer || t.doc.thankYou}</span>
            <span className="issued">
              {t.shared.issued(formatDate(sale.created_at?.slice(0, 10)))}
              {sale.created_by_name ? ` ${t.shared.issuedBy(sale.created_by_name)}` : ''}
            </span>
          </div>
        </div>

        <Card>
          <div className="btn-row no-print">
            <Link className="btn" to={`/customers/${sale.customer_id}`}>
              {t.sale.customerStatement}
            </Link>
            <Link className="btn" to="/sell/new">
              {t.sale.another}
            </Link>
          </div>
        </Card>
      </div>
    </>
  );
}

const Row = ({ k, v }) => (
  <div className="doc-row">
    <span className="k">{k}</span>
    <span className="v">{v}</span>
  </div>
);
